/**
 * DÉTECTION DÉTERMINISTE DES ATTRIBUTIONS JURISPRUDENTIELLES VAGUES.
 *
 * QA : une réponse affirme « la jurisprudence administrative rappelle que… »
 * ou « la Cour de cassation juge de façon constante que… » sans jamais citer
 * de décision identifiable. Ces formules d'autorité, invérifiables, doivent
 * être signalées à l'utilisateur.
 *
 * Le module est PUR (aucune dépendance React ni réseau) et ne modifie aucun
 * prompt : la détection est faite après coup, sur le texte rendu.
 */

/** Une phrase invoquant une autorité jurisprudentielle sans référence. */
export type VagueAttribution = {
  /** Formule détectée, telle qu'écrite dans la réponse. */
  phrase: string;
};

/** Formules d'autorité jurisprudentielle. */
const AUTHORITY_RE = new RegExp(
  [
    `la jurisprudence(?:\\s+(?:constante|[ée]tablie|administrative|judiciaire|civile|sociale|commerciale|p[ée]nale|europ[ée]enne|r[ée]cente|dominante))*`,
    `une jurisprudence(?:\\s+\\w+){0,2}`,
    `les juges(?:\\s+(?:du fond|administratifs|judiciaires|europ[ée]ens))?`,
    `la Cour de cassation`,
    `le Conseil d['’][EÉ]tat`,
    `le Conseil constitutionnel`,
    `la Cour europ[ée]enne des droits de l['’]homme`,
    `la CJUE`,
    `la doctrine(?:\\s+majoritaire|\\s+dominante)?`,
    `les tribunaux`,
  ].join("|"),
  "i",
);

/** Verbes d'affirmation d'autorité. */
const AUTHORITY_VERB_RE =
  /\b(?:juge(?:nt|ait|aient)?|rappelle(?:nt)?|consid[èe]re(?:nt)?|estime(?:nt)?|affirme(?:nt)?|reconna[îi]t|reconnaissent|retient|retiennent|admet(?:tent)?|d[ée]cide(?:nt)?|pr[ée]cise(?:nt)?|exige(?:nt)?|impose(?:nt)?|sanctionne(?:nt)?|a jug[ée]|ont jug[ée]|a rappel[ée]|a consid[ée]r[ée]|a pr[ée]cis[ée]|a admis|a retenu)\b/i;

/**
 * Marqueurs d'une référence identifiable : numéro de pourvoi ou de décision,
 * date d'arrêt, article de code, appel de note. Leur présence dans la phrase
 * suffit à considérer l'affirmation comme rattachée.
 */
const IDENTIFIABLE_RE =
  /(?:n[°ºo]\s?\d|\bart(?:icle)?s?\.?\s+(?:[LRDA]\.?\s?)?\d|\b\d{1,2}\s+(?:janvier|f[ée]vrier|mars|avril|mai|juin|juillet|ao[ûu]t|septembre|octobre|novembre|d[ée]cembre)\s+\d{4}|\[S\d{1,2}\]|[¹²³⁴⁵⁶⁷⁸⁹⁰]|\bECLI\b|\bpourvoi\b|\bBull\.|\bJurisData\b|\bLeb\b)/i;

/**
 * NÉGATION / CONSTAT D'ABSENCE — QA 18/09/2026.
 *
 * « La Cour de cassation n'a jamais jugé qu'une simple idée pouvait être
 * protégée » ou « aucune décision ne retient cette solution » n'invoque AUCUNE
 * autorité : la phrase constate au contraire l'absence de décision. La
 * signaler comme « sans décision citée » induit l'utilisateur en erreur.
 */
/**
 * La négation doit porter sur le VERBE D'AUTORITÉ lui-même. Une négation
 * ordinaire dans le développement (« juge que la clause n'est pas valable »)
 * n'est pas un constat d'absence : l'affirmation reste à sourcer.
 */
const DENIAL_RE = new RegExp(
  [
    `aucun(?:e)?\\s+(?:arr[êe]t|d[ée]cision|jurisprudence|pr[ée]c[ée]dent)`,
    `n['’](?:a|ont)\\s+(?:jamais\\s+|pas\\s+)+(?:jug[ée]|retenu|consid[ée]r[ée]|admis|reconnu|d[ée]cid[ée]|pr[ée]cis[ée]|estim[ée]|rappel[ée])`,
    `ne\\s+(?:juge|jugent|reconna[îi]t|reconnaissent|retient|retiennent|consid[èe]re|consid[èe]rent|admet|admettent|estime|estiment|d[ée]cide|d[ée]cident)\\s+(?:jamais|pas)`,
    `n['’]existe\\s+pas`,
    `jamais\\s+jug[ée]`,
  ].join("|"),
  "i",
);

/** Découpe le texte en paragraphes, en ignorant les blocs de sources. */
function paragraphs(text: string): string[] {
  const idx = text.indexOf("---SOURCES---");
  const body = idx === -1 ? text : text.slice(0, idx);
  return body
    .split(/\n{2,}|\n(?=\s*(?:[-*•]|\d+[.)]))/)
    .map((p) => p.trim())
    .filter(Boolean);
}

/** Découpe un paragraphe en phrases. */
function sentences(paragraph: string): string[] {
  return paragraph
    .replace(/\s+/g, " ")
    // Le point d'une abréviation ("art. L. 1471-1", "Cass. soc.") ne termine
    // pas une phrase : on ne coupe que devant une majuscule.
    .split(/(?<=[.!?])\s+(?=[A-ZÀ-Ý«])/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Renvoie les affirmations d'autorité jurisprudentielle non rattachées à une
 * décision identifiable. Liste bornée : le signalement reste sobre.
 *
 * CAUSE RACINE CORRIGÉE (QA 18/09/2026) : la recherche d'une référence
 * identifiable se limitait à la PHRASE. Une réponse qui cite l'arrêt Pachot
 * (Ass. plén., 7 mars 1986, n° 83-10.477) puis développe le principe sur deux
 * phrases voisines voyait ces phrases signalées « la Cour de cassation — sans
 * décision citée ». La référence est désormais cherchée dans tout le
 * PARAGRAPHE, et les constats d'absence de jurisprudence sont écartés.
 */
export function detectVagueAttributions(text: string, limit = 3): VagueAttribution[] {
  if (!text || text.trim().length < 20) return [];
  const out: VagueAttribution[] = [];
  const seen = new Set<string>();

  for (const paragraph of paragraphs(text)) {
    if (out.length >= limit) break;
    // Une décision identifiable dans le paragraphe rattache les affirmations
    // d'autorité qui l'entourent : rien à signaler ici.
    if (IDENTIFIABLE_RE.test(paragraph)) continue;

    for (const sentence of sentences(paragraph)) {
      if (out.length >= limit) break;
      const authority = sentence.match(AUTHORITY_RE);
      if (!authority) continue;
      if (!AUTHORITY_VERB_RE.test(sentence)) continue;
      if (IDENTIFIABLE_RE.test(sentence)) continue;
      // Constat d'absence de décision : aucune autorité n'est invoquée.
      if (DENIAL_RE.test(sentence)) continue;
      const phrase = authority[0].replace(/\s+/g, " ").trim();
      const key = phrase.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ phrase });
    }
  }

  return out;
}


/** Libellé sobre affiché dans le bandeau de vérification. */
export function vagueAttributionLabel(item: VagueAttribution): string {
  return `${item.phrase} — sans décision citée`;
}
